import { useMemo } from "react";
import { Box, Pagination, Stack, Typography } from "@mui/material";
import FiltrosTemas, { OpcionFiltro } from "./FiltrosTemas";
import TemaLista from "./TemaLista";
import { TemaApp } from "../../types/tema";
import { categoriaDe } from "../../utils/categoria";
import { useFiltrosUrl } from "../../utils/estadoLista";

const PAGE_SIZE = 8;

/** "Todas" elegido a mano: en la URL no puede confundirse con "no hay filtro"
 *  (que significa `estadoInicial`). */
const TODAS = "todas";

/**
 * Lista de temas con filtros y paginación **en memoria**, sobre un arreglo ya
 * cargado. Se usa para "Mis temas", que son pocos y llegan completos; el
 * catálogo, que puede tener cientos, usa `CatalogoRemoto` y filtra en la base.
 *
 * Aquí las categorías salen de los temas recibidos —y está bien: son las únicas
 * que pueden dar resultados dentro de ese conjunto.
 *
 * - `estadoInicial` fija el filtro de disponibilidad por defecto.
 * - `umbralFiltros` oculta la barra de filtros si hay pocos temas (en "Mis
 *   temas" solo aparece con más de 3).
 * - `claveUrl` prefija los parámetros de la URL donde se guardan los filtros y
 *   la página, para no chocar con el otro listado de la misma vista.
 */
export default function CatalogoTemas({
  temas,
  estadoInicial = "",
  umbralFiltros = 0,
  claveUrl = "",
}: {
  temas: TemaApp[];
  estadoInicial?: string;
  umbralFiltros?: number;
  claveUrl?: string;
}) {
  const { leer, pagina, filtrar, irAPagina } = useFiltrosUrl(claveUrl);
  const categoria = leer("categoria");
  const elegido = leer("estado");
  const estado = elegido === TODAS ? "" : elegido || estadoInicial;

  const categorias: OpcionFiltro[] = useMemo(() => {
    const nombres = Array.from(new Set(temas.map(categoriaDe))).sort((a, b) => a.localeCompare(b));
    return nombres.map((n) => ({ value: n, label: n }));
  }, [temas]);

  const mostrarFiltros = temas.length > umbralFiltros;

  // Sin barra de filtros (pocos temas) se muestran todos, ignorando el estado inicial.
  const filtrados = useMemo(
    () =>
      !mostrarFiltros
        ? temas
        : temas.filter((t) => (!categoria || categoriaDe(t) === categoria) && (!estado || t.estado_codigo === estado)),
    [temas, categoria, estado, mostrarFiltros],
  );

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const paginaActual = Math.min(pagina, totalPaginas);
  const visibles = filtrados.slice((paginaActual - 1) * PAGE_SIZE, paginaActual * PAGE_SIZE);

  return (
    <Box>
      {mostrarFiltros && (
        <FiltrosTemas
          categorias={categorias}
          categoria={categoria}
          onCategoria={(v) => filtrar({ categoria: v })}
          estado={estado}
          onEstado={(v) => filtrar({ estado: v || TODAS })}
          total={filtrados.length}
        />
      )}

      {filtrados.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          No hay temas con esos filtros.
        </Typography>
      ) : (
        <>
          <TemaLista temas={visibles} />
          {totalPaginas > 1 && (
            <Stack alignItems="center" sx={{ mt: 2.5 }}>
              <Pagination count={totalPaginas} page={paginaActual} onChange={(_, p) => irAPagina(p)} color="primary" />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
